import {
  ChevronDownIcon,
  ChevronUpIcon,
  CloseIcon,
  EditIcon,
} from "@chakra-ui/icons";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  Editable,
  EditableInput,
  EditablePreview,
  EditableTextarea,
  Flex,
  IconButton,
  Image,
  ListItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  Textarea,
  Tooltip,
  useToast,
} from "@chakra-ui/react";
import {
  ReorderNarrationSpotDirection,
  ReorderNarrationSpotInput,
  SpotType,
} from "~/generated/graphql.types";
import { SpotOrderBadge } from "~/routes/_shared/SpotOrderBadge";
import {
  useReorderNarrationSpotMutation,
  useUpdateSpotMutation,
} from "./index.generated";
import { SpotForRoutingFragment } from "~/routes/_shared/fragments.generated";
import { useRemoveSpotMutation } from "./index.generated";
import { spotTypeJapanese } from "~/routes/_shared/utils";
import { useContext, useEffect, useState } from "react";
import { SpotForSidebar } from "../convertType";
import { AddMarginLeft } from "../ComponentLayouter/AddMarginLeft";
import { WriteLeftDotLine } from "../ComponentLayouter/WriteLeftDotLine";
import {
  EditModeContext,
  UPPER_BOUND_LAT,
  UPPER_BOUND_LON,
  LOWER_BOUND_LAT,
  LOWER_BOUND_LON,
  validationLatLon,
} from "../../..";
import music from "./icons/icon_music.svg";
import { SpotAssetIdEditable } from "../AssetId";

export const SpotListItem = ({
  spot,
  isSelected,
  isActiveReorderButton = false,
  isActiveReorderMoveUpButton = false,
  isActiveReorderMoveDownButton = false,
  onClick,
  resetSpotIdForEdit,
}: {
  spot: SpotForSidebar;
  isSelected: boolean;
  isActiveReorderButton?: boolean;
  isActiveReorderMoveUpButton?: boolean;
  isActiveReorderMoveDownButton?: boolean;
  onClick: () => void;
  resetSpotIdForEdit: () => void;
}) => {
  const Mergin = spot.parentSpotId
    ? AddMarginLeft
    : spot.spotType === SpotType.RoutingGuide
    ? WriteLeftDotLine
    : Flex;

  const [reorderRes, reorderNarrationSpotmutation] =
    useReorderNarrationSpotMutation();
  const toast = useToast();

  useEffect(() => {
    if (reorderRes.data?.reorderNarrationSpot) {
      toast({
        title: "スポットの順番を変更しました。",
        status: "success",
      });
    }
  }, [reorderRes]);

  return (
    <Mergin>
      <Flex w="full">
        <Flex
          flexDir="column"
          h="full"
          justifyContent="center"
          gap="24px"
          width="24px"
        >
          {isActiveReorderButton && isActiveReorderMoveUpButton && (
            <IconButton
              rounded={"full"}
              icon={<ChevronUpIcon />}
              bg="transparent"
              borderColor="white"
              textColor="white"
              borderWidth="1px"
              size="20px"
              w="20px"
              aria-label="reorder-up"
              onClick={(e) => {
                e.stopPropagation();
                const input: ReorderNarrationSpotInput = {
                  courseId: spot.courseId,
                  spotId: spot.id,
                  direction: ReorderNarrationSpotDirection.Up,
                };
                reorderNarrationSpotmutation(
                  { input },
                  {
                    additionalTypenames: ["Course"],
                  }
                );
              }}
            />
          )}
          {isActiveReorderButton && isActiveReorderMoveDownButton && (
            <IconButton
              rounded={"full"}
              icon={<ChevronDownIcon />}
              bg="transparent"
              borderColor="white"
              textColor="white"
              borderWidth="1px"
              aria-label="reorder-down"
              size="20px"
              w="20px"
              onClick={(e) => {
                e.stopPropagation();
                const input: ReorderNarrationSpotInput = {
                  courseId: spot.courseId,
                  spotId: spot.id,
                  direction: ReorderNarrationSpotDirection.Down,
                };
                reorderNarrationSpotmutation(
                  {
                    input,
                  },
                  {
                    additionalTypenames: ["Course"],
                  }
                );
              }}
            />
          )}
        </Flex>
        <ListItem
          data-testid={"spot-list-item"}
          my={2}
          p={spot.spotType === SpotType.RoutingGuide ? 1.5 : 3}
          flex={1}
          color="white"
          bgColor="#071223"
          justifyContent={"space-between"}
          borderColor="pink.600"
          borderWidth={isSelected ? 3 : 0}
          onClick={onClick}
        >
          <Flex w="full" justifyContent="space-between">
            <Flex flexGrow={1} flexDir="column">
              <Flex w="full" justifyContent="space-between">
                <Flex alignItems="center">
                  <SpotOrderBadge label={spot.spotOrderLabel} spot={spot} />
                  <SpotTypeDisplay spot={spot} />
                  {spot.hasMusic ? <Image src={music} h={8} /> : null}
                </Flex>
                {spot.isRemovable ? (
                  <RemoveButton
                    spot={spot}
                    resetSpotIdForEdit={resetSpotIdForEdit}
                  />
                ) : null}
              </Flex>

              {spot.isNameEditable ? <NameEditable spot={spot} /> : null}
              {spot.isCoordinateEditable ? (
                <CoordinateEditable spot={spot} />
              ) : null}
              {spot.isAssetEditable ? (
                <SpotAssetIdEditable spot={spot} />
              ) : null}
              {spot.isMemoEditable ? <MemoEditor spot={spot} /> : null}
            </Flex>
          </Flex>
        </ListItem>
      </Flex>
    </Mergin>
  );
};

export const SpotTypeDisplay = ({ spot }: { spot: SpotForSidebar }) => {
  const [updateSpotRes, updateSpot] = useUpdateSpotMutation();

  const toast = useToast();
  useEffect(() => {
    if (updateSpotRes.data) {
      toast({
        title: "スポットのタイプを変更しました。",
        status: "success",
      });
    }
  }, [updateSpotRes.data]);
  if (spot.isSpotTypeEditable) {
    if (spot.parentSpotId) {
      return (
        <Tooltip
          label="このスポットのタイプを変更"
          hasArrow
          data-testid={"spot-type-selector"}
        >
          <Select
            size="xs"
            w={36}
            variant="filled"
            bgColor="blue.800"
            ml={2}
            value={spot.spotType}
            onChange={(e) => {
              updateSpot(
                {
                  input: {
                    id: spot.id,
                    spotType: e.target.value as SpotType,
                    parentSpotId:
                      e.target.value === SpotType.OptionalWaypoint
                        ? spot.id
                        : undefined,
                  },
                },
                { additionalTypenames: ["RoutingInfomationPayload"] }
              );
            }}
            data-testid={"spot-type-selector"}
          >
            <>
              <option value={SpotType.RoutingGuide}>ルート誘導用</option>
              <option value={SpotType.SubaruMoment}>スバルモーメント</option>
            </>
          </Select>
        </Tooltip>
      );
    } else {
      return (
        <Tooltip
          label="このスポットのタイプを変更"
          hasArrow
          data-testid={"spot-type-selector"}
        >
          <Select
            size="xs"
            w={36}
            variant="filled"
            bgColor="blue.800"
            ml={2}
            value={spot.spotType}
            onChange={(e) => {
              updateSpot(
                {
                  input: {
                    id: spot.id,
                    spotType: e.target.value as SpotType,
                    parentSpotId:
                      e.target.value === SpotType.OptionalWaypoint
                        ? spot.id
                        : undefined,
                  },
                },
                { additionalTypenames: ["RoutingInfomationPayload"] }
              );
            }}
            data-testid={"spot-type-selector"}
          >
            <>
              <option value={SpotType.Waypoint}>経由地</option>
              <option value={SpotType.OptionalWaypoint}>
                オプションの経由地
              </option>
              <option value={SpotType.RoutingGuide}>ルート誘導用</option>
              <option value={SpotType.SubaruMoment}>スバルモーメント</option>
            </>
          </Select>
        </Tooltip>
      );
    }
  }

  return <Text>{spotTypeJapanese.get(spot.spotType)}</Text>;
};

const CoordinateEditable = ({ spot }: { spot: SpotForRoutingFragment }) => {
  const toast = useToast();
  const [updateSpotRes, updateSpot] = useUpdateSpotMutation();

  return (
    <Editable
      fontSize="xs"
      color="gray"
      defaultValue={`${spot.coordinate.lat
        .toString()
        .slice(0, 12)},${spot.coordinate.lon.toString().slice(0, 12)}`}
      data-testid="coordinate-editable"
      onSubmit={(coordinateString) => {
        // TODO: ここでバリデーションをかける
        if (coordinateString.split(",").length !== 2) {
          toast({
            status: "error",
            title: "入力値が不正です。",
            description: "緯度経度は「緯度,経度」の形式で入力してください。",
          });
          return;
        }
        if (coordinateString.replace(/[^\d\.,]/g, "") !== coordinateString) {
          toast({
            status: "error",
            title: "入力値が不正です。",
            description: "半角の数字を用いて入力してください。",
          });
          return;
        }
        const lat = Number(coordinateString.split(",")[0]);
        const lon = Number(coordinateString.split(",")[1]);

        const isValidLocation = validationLatLon({ lat, lon });
        if (!isValidLocation.isValid) {
          toast({
            status: "error",
            title: "入力値が不正です。",
            description: isValidLocation.message,
          });
          return;
        }

        updateSpot(
          {
            input: {
              id: spot.id,
              coordinate: {
                lat: Number(
                  coordinateString.split(",")[0].replace(/[^\d\.]/g, "")
                ),
                lon: Number(
                  coordinateString.split(",")[1].replace(/[^\d\.]/g, "")
                ),
              },
            },
          },
          {
            additionalTypenames: ["Course", "RoutingInfomationPayload"],
          }
        );
      }}
    >
      <EditablePreview />
      <EditableInput />
    </Editable>
  );
};

const NameEditable = ({ spot }: { spot: SpotForRoutingFragment }) => {
  const toast = useToast();
  const [updateSpotRes, updateSpot] = useUpdateSpotMutation();

  useEffect(() => {
    if (updateSpotRes.data) {
      toast({
        title: "スポットの名前を変更しました。",
        status: "success",
      });
    }
  }, [updateSpotRes.data]);
  return (
    <Editable
      data-testid={"name-editable"}
      fontSize="sm"
      mt={2}
      defaultValue={spot.name || "名称未設定"}
      onSubmit={(newName) => {
        updateSpot(
          {
            input: {
              id: spot.id,
              name: newName,
            },
          },
          { additionalTypenames: ["Course"] }
        );
      }}
    >
      <EditablePreview w="full" />
      <EditableTextarea />
    </Editable>
  );
};

const MemoEditor = ({ spot }: { spot: SpotForRoutingFragment }) => {
  const toast = useToast();
  const [updateSpotRes, updateSpot] = useUpdateSpotMutation();

  useEffect(() => {
    if (updateSpotRes.data) {
      toast({
        title: "スポットのメモを変更しました。",
        status: "success",
      });
    }
  }, [updateSpotRes.data]);

  return (
    <Accordion allowToggle data-testid="memo-editor">
      <AccordionItem border="none">
        <h2>
          <AccordionButton px={0}>
            <Box textAlign="left" fontSize={"xs"}>
              メモ
            </Box>
            <AccordionIcon />
          </AccordionButton>
        </h2>
        <AccordionPanel px={0} py={2} fontSize={"xs"}>
          <Flex>{spot.note}</Flex>
          <Flex direction={"row-reverse"}>
            <EditSpotMemoButton
              onSubmitNote={(inputNote) => {
                updateSpot(
                  {
                    input: {
                      id: spot.id,
                      note: inputNote,
                    },
                  },
                  {
                    additionalTypenames: ["Course"],
                  }
                );
              }}
              note={spot.note || ""}
            />
          </Flex>
        </AccordionPanel>
      </AccordionItem>
    </Accordion>
  );
};

export const RemoveButton = ({
  spot,
  resetSpotIdForEdit,
}: {
  spot: SpotForRoutingFragment;
  resetSpotIdForEdit: () => void;
}) => {
  const toast = useToast();

  const [removeSpotRes, removeSpot] = useRemoveSpotMutation();
  useEffect(() => {
    if (removeSpotRes.data) {
      toast({
        title: "スポットを削除しました。",
        status: "success",
      });
    }
  }, [removeSpotRes.data]);

  return (
    <IconButton
      aria-label="remove"
      icon={<CloseIcon fontSize="xs" />}
      colorScheme="white"
      rounded="full"
      size="xs"
      data-testid={"remove-button"}
      onClick={(e) => {
        e.stopPropagation();
        resetSpotIdForEdit();
        removeSpot(
          { input: { id: spot.id } },
          {
            additionalTypenames: ["Course", "RoutingInfomationPayload"],
          }
        );
      }}
    />
  );
};

export const EditSpotMemoButton = ({
  onSubmitNote,
  note,
}: {
  onSubmitNote: (inputNote: string) => void;
  note: string;
  dataTestid?: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [newNote, setNewNote] = useState(note);

  return (
    <>
      <IconButton
        aria-label="edit note"
        onClick={() => setIsOpen(true)}
        icon={<EditIcon />}
        colorScheme="white"
      />
      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>メモ</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Textarea
              onChange={(e) => setNewNote(e.target.value)}
              defaultValue={newNote}
            />
          </ModalBody>

          <ModalFooter>
            <Button
              colorScheme="blue"
              onClick={() => {
                onSubmitNote(newNote);
                setIsOpen(false);
              }}
            >
              完了
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
